import { Formik } from 'formik';
import Select, { ActionMeta } from 'react-select';
import removeIcon from 'images/icons/svg/error.svg';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toaster } from 'components/v2/toast';
import AuthService from 'services/AuthService';
import CompanyService from 'services/CompanyService';
import { CompanyModel, CreateMembershipModel, MembershipModel, RoleModel } from 'services/EpdClient';
import MembershipService from 'services/MembershipService';
import AdminService from 'services/admin/AdminService';
import { ButtonSmall, LabelBox, StyledReactSelect } from 'styles/Styles.styled';
import { Option } from 'types/types';
import { getApiErrorDetail } from 'util/apiErrorUtils';
import RemoveMembershipDialog from './RemoveMembershipDialog';
import { getOrganisationRemovalContent, getRoleRemovalContent, RemovalDialogContent } from './removalContent';
import { AdminGridForm, CompaniesListBox, CompaniesListItem, FixedLabel, ImageRemove } from './style';

interface Props {
  userid: string;
}

/** A removal waiting for the Super Admin's confirmation: what the dialog shows and what Delete runs. */
type PendingRemoval = { content: RemovalDialogContent; remove: () => Promise<void> };

const EditUserMembership: React.FunctionComponent<Props> = ({ userid }) => {
  const { t } = useTranslation();
  const [companiesLookup, setCompaniesLookup] = useState<CompanyModel[]>([]);
  const [memberships, setMemberships] = useState<MembershipModel[]>([]);
  const [roles, setRoles] = useState<RoleModel[] | undefined>();
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  // True while a create or removal and the reload after it run; the row selectors are locked meanwhile.
  const [busy, setBusy] = useState(false);

  const fetchUserMemberships = async () => {
    const result = await MembershipService.getMembershipsByUser(userid);
    setMemberships(result);
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      const companiesResult = await CompanyService.getCompanies();
      setCompaniesLookup(companiesResult);
    };

    const loadRoles = async () => {
      const result = await AdminService.getRoles();
      setRoles(result);
    };

    fetchUserMemberships();
    fetchCompanies();
    loadRoles();
  }, [userid]);

  const initialValues = {
    newCompany: '',
  };

  const showError = (error: unknown, fallbackKey: string) => {
    toaster({
      severity: 'error',
      summary: 'Error',
      details: getApiErrorDetail(error) ?? t(fallbackKey),
    });
  };

  // Reloads the list after a change; a failed reload gets its own toast so the caller can still finish.
  const reloadMemberships = async (fallbackKey: string) => {
    try {
      await fetchUserMemberships();
    } catch (error) {
      showError(error, fallbackKey);
    }
  };

  // Adds a membership and reloads. The page stays busy until the list on screen is current, so the same role
  // cannot be picked twice while the POST and the reload run.
  const createMembership = async (createModel: CreateMembershipModel) => {
    setBusy(true);
    try {
      await MembershipService.create(createModel);
    } catch (error) {
      showError(error, 'epdWizard.messages.error');
    } finally {
      await reloadMemberships('epdWizard.messages.error');
      setBusy(false);
    }
  };

  const addCompany = async (company: Option) => {
    if (busy) {
      return;
    }
    const defaultRole = roles?.find((x) => x.name === 'EPD Owner');
    await createMembership({
      companyId: company.value,
      userId: userid,
      roleId: defaultRole?.id,
    });
  };

  // Delete in the dialog: runs the pending removal, shows the error if any, and always reloads. The dialog awaits
  // this, so Delete keeps its spinner and the dialog stays open until the list on screen is current.
  const confirmRemoval = async () => {
    if (!pendingRemoval) {
      return;
    }
    setBusy(true);
    try {
      await pendingRemoval.remove();
    } catch (error) {
      showError(error, 'account.removeMembership.errorFallback');
    } finally {
      await reloadMemberships('account.removeMembership.errorFallback');
      setPendingRemoval(null);
      setBusy(false);
    }
  };

  // The selector is controlled: it shows the loaded memberships, so a change shows up only after a reload.
  const handleRolesSelectorChange = async (rowMemberships: MembershipModel[], actionMeta: ActionMeta<Option>) => {
    if (busy) {
      return;
    }
    switch (actionMeta.action) {
      case 'select-option':
        if (actionMeta.option) {
          await createMembership({
            companyId: rowMemberships[0].companyId,
            userId: userid,
            roleId: actionMeta.option.value,
          });
        }
        return;
      // The chip "x" and Backspace only ask for confirmation; the dialog's Delete removes the role.
      case 'remove-value':
      case 'pop-value': {
        const removedRoleId = actionMeta.removedValue.value;
        const removed = rowMemberships.find((m) => m.roleId === removedRoleId);
        if (removed) {
          setPendingRemoval({
            content: getRoleRemovalContent(rowMemberships, removed),
            remove: () => MembershipService.deleteMembership(removed.id),
          });
        }
        return;
      }
      default:
        // 'clear' cannot happen (isClearable={false}); no other action adds or removes a role.
        return;
    }
  };

  const withBreaks = (s: string | null) => s?.replaceAll('_', '_\u200B') ?? '';

  return (
    <>
      <Formik id={'formik2'} initialValues={initialValues} onSubmit={() => {}} enableReinitialize>
        {({ values, setFieldValue }) => (
          <AdminGridForm>
            <LabelBox>
              <FixedLabel>{t('account.companies')}</FixedLabel>
            </LabelBox>

            <FixedLabel style={{ marginLeft: '24px' }}>{t('account.roles')}</FixedLabel>

            <CompaniesListBox>
              {_.values(_.groupBy(memberships, (i) => i.companyId)).map((c) => {
                return (
                  <CompaniesListItem key={c[0].companyId}>
                    <LabelBox>
                      <FixedLabel
                        style={{
                          whiteSpace: 'normal',
                          overflowWrap: 'normal',
                          wordBreak: 'keep-all',
                        }}
                      >
                        {withBreaks(c[0].companyName)}
                      </FixedLabel>
                    </LabelBox>
                    <Select
                      isDisabled={!AuthService.isAdmin() || busy}
                      isLoading={busy}
                      name={c[0].companyId}
                      onChange={(_newOptions, actionMeta) => handleRolesSelectorChange(c, actionMeta)}
                      options={roles?.map((r) => {
                        return { value: r.id!, label: r.name! } satisfies Option;
                      })}
                      value={c.map((m) => {
                        return { value: m.roleId, label: m.roleName as string } satisfies Option;
                      })}
                      isMulti
                      isClearable={false}
                    />
                    {AuthService.isAdmin() && (
                      <ImageRemove>
                        <img
                          src={removeIcon}
                          style={{ height: '1rem' }}
                          alt="Remove"
                          onClick={() => {
                            if (busy) {
                              return;
                            }
                            // Only asks for confirmation; the dialog's Delete removes the user from the organisation.
                            setPendingRemoval({
                              content: getOrganisationRemovalContent(c),
                              remove: () => MembershipService.removeUserFromCompany(c[0].companyId, userid),
                            });
                          }}
                        />
                      </ImageRemove>
                    )}
                  </CompaniesListItem>
                );
              })}
            </CompaniesListBox>
            {AuthService.isAdmin() && (
              <>
                <LabelBox>
                  <FixedLabel>{t('account.connectToOrganization')}</FixedLabel>
                </LabelBox>
                <StyledReactSelect
                  defaultValue={initialValues.newCompany}
                  name="newCompany"
                  options={companiesLookup?.map((company) => {
                    return { value: company.id, label: company.name } as Option;
                  })}
                  isClearable
                />
                <ButtonSmall
                  style={{ padding: '0.5rem 1rem', borderRadius: '3px' }}
                  type="button"
                  disabled={values.newCompany == null || busy}
                  onClick={() => {
                    addCompany(values.newCompany as any);
                    setFieldValue('newCompany', initialValues.newCompany);
                  }}
                >
                  +
                </ButtonSmall>
              </>
            )}
          </AdminGridForm>
        )}
      </Formik>
      <RemoveMembershipDialog
        content={pendingRemoval?.content ?? null}
        onConfirm={confirmRemoval}
        onClose={() => setPendingRemoval(null)}
      />
    </>
  );
};

export default EditUserMembership;
